/**
 * Uses `geoAxios` — a dedicated instance, deliberately not the shared
 * `axiosInstance`/`ApiClient` — see `geoAxios.ts` for why.
 */
import { createRateLimiter } from '@/common/utils/createRateLimiter';
import { GEOLOCATION_CONFIG } from '@/features/geolocation/config';
import type { GeoCoordinates } from '@/features/geolocation/types/location';
import { geoAxios } from './geoAxios';
import { elevationResponseSchema } from './schemas';

const elevationLimiter = createRateLimiter(() => GEOLOCATION_CONFIG.elevation.minIntervalMs);

export interface ElevationReading {
  metres: number;
  /**
   * Open-Elevation returns 0 for "no data", which is indistinguishable from a
   * genuine sea-level reading — so an exact 0 is flagged for the user to verify
   * rather than trusted silently.
   */
  isSuspect: boolean;
}

export const ElevationService = {
  getElevation: async (
    coordinates: GeoCoordinates,
    signal?: AbortSignal
  ): Promise<ElevationReading> => {
    const params = new URLSearchParams({
      locations: `${coordinates.latitude},${coordinates.longitude}`,
    });

    const url = `${GEOLOCATION_CONFIG.elevation.baseUrl}/api/v1/lookup?${params.toString()}`;

    const response = await elevationLimiter.schedule(
      () => geoAxios.get(url, { signal, headers: { Accept: 'application/json' } }),
      signal
    );

    const parsed = elevationResponseSchema.parse(response.data);
    const reading = parsed.results[0];

    if (!reading) {
      throw new Error('Open-Elevation returned no results');
    }

    return { metres: reading.elevation, isSuspect: reading.elevation === 0 };
  },
};
